import { FlowType } from '@cash-web/protos/squareup/cash/plasma/flows/FlowProto.pb'
import { ClientScenario } from '@cash-web/protos/squareup/franklin/api/client_scenario.pb'
import { BlockerAction } from '@cash-web/protos/squareup/franklin/blocker_action.pb'
import { ResponseContext } from '@cash-web/protos/squareup/franklin/ResponseContextProto.pb'
import {
  BlockerType,
  FlowBlockerDescriptor,
  FlowStartProps,
  FlowUpdateProps,
  getBlockerDataDetail,
  getBlockerType,
} from '@cash-web/shared-flow-data-access-context'
import { createSingleUseFlowToken } from '@cash-web/shared-util-fetch'
import { RouteInfo } from '@cash-web/shared-util-route-info'
import { BlockerFlowInteractViewBlockerResponseStatus } from '@squareup/customer-data-events-web'

import { FlowResponse } from '../middleware/utils'
import { getBlockerDescriptorsFromAction } from '../utils'

export type FlowStateExternal = {
  blockerActionView?: BlockerAction

  error?: {
    message?: string
    status?: string
  }

  isInProgress: boolean

  isLoading: boolean

  paymentTokens?: string[]

  transferToken?: string
}

// backwards compatibility exports
export type FlowCompatExternal = {
  activeBlockerDescriptor?: FlowBlockerDescriptor

  blockerDescriptors: FlowBlockerDescriptor[]

  clientScenario?: ClientScenario

  flowToken: string

  responseContext?: ResponseContext

  trackingFlowName?: string
}

export type FlowStateInternal = {
  blockerSubmitStartTime?: number

  blockerViewStartTime?: number

  flowStartTime?: number

  originatingFlowType?: FlowType

  // the collection of blockers / actions we went through
  path: string[]

  skippedBlockers: BlockerType[]
}
type State = FlowStateInternal & FlowStateExternal & FlowCompatExternal
export type FlowState = State & {
  suspended: Record<string, FlowState>
}

export type FlowReducerAction =
  | ({
      flowType?: FlowType
      type: 'FLOW_START'
    } & FlowStartProps)
  | {
      error?: {
        errorCode?: string
        errorMessage?: string
      }
      requestPath?: string
      response: FlowResponse
      status: BlockerFlowInteractViewBlockerResponseStatus
      statusCode?: number
      type: 'FLOW_RESPONSE'
    }
  | { type: 'FLOW_COMPLETE' }
  | { force?: boolean; onCancelFlowOverride?: () => void; type: 'FLOW_CANCEL' }
  | { type: 'FLOW_RESET' }
  | { id: string; type: 'FLOW_SUSPEND' }
  | { name?: string; type: 'FLOW_SET_NAME' }
  | { id: string; type: 'FLOW_RESTORE' }
  | ({ type: 'FLOW_UPDATE' } & FlowUpdateProps)
  | { routeInfo?: RouteInfo; trackingName?: string; type: 'LOAD_URL'; url?: string }
  | { blockerAction: BlockerAction; trackingName?: string; type: 'BLOCKER_ACTION_EXECUTE' }
  | { blockerDescriptor: FlowBlockerDescriptor; type: 'BLOCKER_VIEW' }
  | { requestPath?: string; type: 'BLOCKER_SUBMIT' }
  | { blockerDescriptors: FlowBlockerDescriptor[]; requestPath?: string; type: 'BLOCKER_SUBMIT_SUCCESS' }
  | {
      blockerDescriptors?: FlowBlockerDescriptor[]
      message?: string
      requestPath?: string
      status?: string
      type: 'BLOCKER_SUBMIT_ERROR'
    }
  | { type: 'BLOCKER_SKIP_REMOTE' }
  | { type: 'BLOCKER_SKIP_LOCAL' }
  | { blockerActionView: BlockerAction; type: 'BLOCKER_ACTION_VIEW' }
  | { type: 'BLOCKER_ACTION_CLOSE' }
  | { isLoading: boolean; type: 'SET_LOADING' }
  | { clientScenario?: ClientScenario; type: 'SET_CLIENT_SCENARIO' }
  | { blockerDescriptors: FlowBlockerDescriptor[]; type: 'SET_BLOCKER_DESCRIPTORS' }
  | { flowToken: string; type: 'SET_FLOW_TOKEN' }
  | { paymentTokens: string[]; type: 'SET_PAYMENT_TOKENS' }
  | { transferToken: string; type: 'SET_TRANSFER_TOKEN' }

const getDefaultState = (): FlowState => ({
  suspended: {},
  trackingFlowName: undefined,
  activeBlockerDescriptor: undefined,
  blockerDescriptors: [],
  path: [],
  skippedBlockers: [],
  flowStartTime: undefined,
  blockerViewStartTime: undefined,
  clientScenario: undefined,
  responseContext: undefined,
  paymentTokens: undefined,
  transferToken: undefined,
  blockerActionView: undefined,
  flowToken: createSingleUseFlowToken(),
  isInProgress: false,
  isLoading: false,
})
export const flowReducer = (state: FlowState, action: FlowReducerAction): FlowState => {
  switch (action.type) {
    case 'BLOCKER_ACTION_EXECUTE': {
      return {
        ...state,
        isLoading: true,
      }
    }
    case 'BLOCKER_SKIP_LOCAL': {
      if (!state?.activeBlockerDescriptor) {
        return state
      }
      const type = getBlockerType(state?.activeBlockerDescriptor?.blocker)
      // update the skipped blockers array
      const skippedBlockers = [...new Set([...state.skippedBlockers, type])]
      const blockerDescriptors = state.blockerDescriptors.slice(1)
      return {
        ...state,
        blockerDescriptors,
        skippedBlockers: skippedBlockers,
        isLoading: false,
      }
    }
    case 'BLOCKER_SUBMIT': {
      return {
        ...state,
        isLoading: true,
        // we are no longer viewing the blocker
        blockerViewStartTime: undefined,
        // we are submitting the blocker
        blockerSubmitStartTime: Date.now(),
      }
    }
    case 'BLOCKER_SUBMIT_ERROR': {
      const blockerDescriptors = getBlockerDescriptorsFromAction(action) ?? []
      const hasBlockers = blockerDescriptors?.length > 0
      return {
        ...state,
        error: {
          message: action.message,
          status: action.status,
        },
        // we only want to update blockers on error if we were given new blockers to display
        blockerDescriptors: hasBlockers ? blockerDescriptors : state.blockerDescriptors,
        isLoading: false,
      }
    }
    case 'BLOCKER_SUBMIT_SUCCESS': {
      const blockerDescriptors = getBlockerDescriptorsFromAction(action) ?? []
      return {
        ...state,
        blockerDescriptors,
        isLoading: false,
      }
    }
    case 'BLOCKER_VIEW': {
      const blockerDetail = getBlockerDataDetail(action.blockerDescriptor)
      const name = blockerDetail.id ?? blockerDetail.trackingName
      const lastPath = state.path[state.path.length - 1]
      return {
        ...state,
        activeBlockerDescriptor: action.blockerDescriptor,
        blockerViewStartTime: Date.now(),
        // we only want to add to the path if we've changed blockers and not showing the same blocker
        path: lastPath !== name ? [...state.path, name] : state.path,
      }
    }
    case 'FLOW_CANCEL':
    case 'FLOW_COMPLETE': {
      const dismissAction = state.activeBlockerDescriptor?.dismiss_action

      if (action.type === 'FLOW_CANCEL' && action.force !== true && dismissAction != null) {
        // we do not want to cancel if we have a dismiss action unless force is true
        return state
      }

      return {
        ...state,
        ...getDefaultState(),
        suspended: state.suspended,
      }
    }
    case 'BLOCKER_ACTION_VIEW': {
      return {
        ...state,
        isLoading: false,
        blockerActionView: action.blockerActionView,
      }
    }
    case 'BLOCKER_ACTION_CLOSE': {
      return {
        ...state,
        blockerActionView: undefined,
      }
    }
    case 'FLOW_RESPONSE': {
      return {
        ...state,
        responseContext: action.response.response_context,
        blockerSubmitStartTime: undefined,
      }
    }
    case 'FLOW_START': {
      const blockerDescriptors = getBlockerDescriptorsFromAction(action) ?? []
      return {
        ...state,
        trackingFlowName: action.trackingFlowName,
        originatingFlowType: action.originatingFlow ? action.originatingFlow.flowType : action.flowType,
        clientScenario: 'clientScenario' in action ? action.clientScenario : state.clientScenario,
        flowToken: action.flowToken ?? state.flowToken,
        blockerDescriptors,
        isInProgress: blockerDescriptors.length > 0 || action.responseContext?.status_result != null,
        flowStartTime: Date.now(),
        paymentTokens: action.paymentTokens,
        transferToken: action.transferToken,
        responseContext: action.responseContext ?? state.responseContext,
        isLoading: false,
        // reset skipped blockers when starting a new flow
        skippedBlockers: [],
      }
    }
    case 'FLOW_SET_NAME': {
      return {
        ...state,
        trackingFlowName: action.name,
      }
    }
    case 'FLOW_RESET': {
      return {
        ...getDefaultState(),
      }
    }
    case 'FLOW_SUSPEND': {
      return {
        ...getDefaultState(),
        suspended: {
          ...state.suspended,
          [action.id]: state,
        },
      }
    }
    case 'FLOW_RESTORE': {
      const { [action.id]: suspendedFlowState, ...suspended } = state.suspended
      if (suspendedFlowState != null) {
        // if we have a suspended flow state, we want to restore it
        return {
          ...suspendedFlowState,
          suspended,
        }
      }
      return state
    }
    case 'FLOW_UPDATE': {
      const blockerDescriptors = getBlockerDescriptorsFromAction(action) ?? []
      return {
        ...state,
        flowToken: action.flowToken ?? state.flowToken,
        clientScenario:
          'clientScenario' in action && action.clientScenario ? action.clientScenario : state.clientScenario,
        blockerDescriptors,
        isInProgress: blockerDescriptors.length > 0 || action.responseContext?.status_result != null,
        paymentTokens: action.paymentTokens,
        transferToken: action.transferToken,
        responseContext: action.responseContext ?? state.responseContext,
        isLoading: false,
      }
    }

    case 'SET_BLOCKER_DESCRIPTORS': {
      return {
        ...state,
        blockerDescriptors: action.blockerDescriptors,
        isInProgress: true,
        skippedBlockers: [],
      }
    }
    case 'SET_CLIENT_SCENARIO': {
      return {
        ...state,
        clientScenario: action.clientScenario,
      }
    }
    case 'SET_FLOW_TOKEN': {
      return {
        ...state,
        flowToken: action.flowToken,
      }
    }
    case 'SET_LOADING': {
      return {
        ...state,
        isLoading: action.isLoading,
      }
    }
    case 'SET_PAYMENT_TOKENS': {
      return {
        ...state,
        paymentTokens: action.paymentTokens,
      }
    }
    case 'SET_TRANSFER_TOKEN': {
      return {
        ...state,
        transferToken: action.transferToken,
      }
    }
    default:
      break
  }

  return state
}
